<template>
  <div class="ec-list">

    <admin-table-subheader
      v-model="search"
      title="Applications"
      search
      search-placeholder="Search applications…"
      :refresh-button="true"
      :refresh-loading="loading"
      @refresh="initialize"
    />

    <div class="pa-filters">
      <button
        v-for="f in filters"
        :key="f.value"
        class="pa-filter"
        :class="{ 'pa-filter--on': status === f.value }"
        @click="setStatus(f.value)"
      >
        {{ f.label }}
        <span v-if="counts[f.value] !== undefined" class="pa-filter__count">{{ counts[f.value] }}</span>
      </button>
    </div>

    <div v-if="loading" class="ec-loading">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <v-data-table
      v-else
      :headers="headers"
      :items="applications"
      :search="search"
      :items-per-page="30"
      :footer-props="{ 'items-per-page-options': [10, 30, 50, -1] }"
      class="ec-table"
    >
      <template v-slot:[`item.company_name`]="{ item }">
        <strong>{{ item.company_name }}</strong>
      </template>

      <template v-slot:[`item.contact_name`]="{ item }">
        <div>{{ item.contact_name }}</div>
        <a :href="`mailto:${item.email}`" class="pa-email">{{ item.email }}</a>
      </template>

      <template v-slot:[`item.partner_type`]="{ item }">
        <span class="u-date">{{ item.partner_type || '—' }}</span>
      </template>

      <template v-slot:[`item.country`]="{ item }">
        <span class="u-date">{{ item.country || '—' }}</span>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <span class="u-status-pill" :class="pillClass(item.status)">
          <v-icon size="11" style="margin-right:3px">{{ statusIcon(item.status) }}</v-icon>
          {{ item.status }}
        </span>
      </template>

      <template v-slot:[`item.created_at`]="{ item }">
        <span class="u-date">{{ fmtDate(item.created_at) }}</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <button class="ec-icon-btn ec-icon-btn--primary" title="Details" @click="openDetail(item)">
          <v-icon size="15">mdi-eye-outline</v-icon>
        </button>
        <button
          v-if="item.status === 'pending'"
          class="ec-icon-btn"
          title="Approve"
          @click="openApprove(item)"
        >
          <v-icon size="15">mdi-check-circle-outline</v-icon>
        </button>
        <button
          v-if="item.status === 'pending'"
          class="ec-icon-btn ec-icon-btn--danger"
          title="Reject"
          @click="askReject(item)"
        >
          <v-icon size="15">mdi-close-circle-outline</v-icon>
        </button>
      </template>
    </v-data-table>

    <partner-application-detail
      v-if="showDetail"
      :show="showDetail"
      :application="selected"
      @close="showDetail = false"
    />

    <partner-application-approve
      v-if="showApprove"
      :show="showApprove"
      :application="selected"
      @close="showApprove = false"
      @approved="onApproved"
      @notify="notify"
    />

    <confirm-dialog
      :show.sync="confirm.show"
      :title="confirm.title"
      :message="confirm.message"
      :item-name="confirm.itemName"
      :loading="confirm.loading"
      confirm-text="Reject"
      cancel-text="Cancel"
      type="danger"
      @confirm="doReject"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { apiService } from '../../service/api.service'
import AdminTableSubheader from '../shared/AdminTableSubheader.vue'
import ConfirmDialog from '../shared/ConfirmDialog.vue'
import PartnerApplicationDetail from './PartnerApplicationDetail.vue'
import PartnerApplicationApprove from './PartnerApplicationApprove.vue'

export default {
  name: 'PartnerApplicationTable',
  components: {
    AdminTableSubheader,
    ConfirmDialog,
    PartnerApplicationDetail,
    PartnerApplicationApprove
  },
  data () {
    return {
      applications: [],
      counts: {},
      loading: false,
      search: null,
      status: 'pending',
      showDetail: false,
      showApprove: false,
      selected: null,
      confirm: { show: false, title: '', message: '', itemName: '', loading: false, target: null },
      snackbar: { show: false, text: '', color: 'success' },
      filters: [
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
        { label: 'All', value: '' }
      ],
      headers: [
        { text: 'Company', value: 'company_name', align: 'start' },
        { text: 'Contact', value: 'contact_name', align: 'start' },
        { text: 'Type', value: 'partner_type', align: 'center' },
        { text: 'Country', value: 'country', align: 'center' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Submitted', value: 'created_at', align: 'center' },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ]
    }
  },
  mounted () {
    this.initialize()
  },
  methods: {
    notify (text, color = 'success') {
      this.snackbar = { show: true, text, color }
    },
    fmtDate (v) {
      if (!v) return '—'
      const d = new Date(v)
      return isNaN(d) ? v : d.toLocaleDateString()
    },
    pillClass (status) {
      if (status === 'approved') return 'u-pill--green'
      if (status === 'rejected') return 'u-pill--red'
      return 'u-pill--amber'
    },
    statusIcon (status) {
      if (status === 'approved') return 'mdi-check'
      if (status === 'rejected') return 'mdi-close'
      return 'mdi-clock-outline'
    },
    setStatus (value) {
      this.status = value
      this.initialize()
    },
    async initialize () {
      this.loading = true
      const query = this.status ? `?status=${this.status}` : ''
      await apiService.get(`admin/partner-applications${query}`)
        .then((res) => {
          this.applications = res.data || []
          // Only the unfiltered listing can report counts for every status.
          if (!this.status) this.counts = this.tally(this.applications)
          else this.$set(this.counts, this.status, this.applications.length)
        })
        .catch(() => this.notify('Failed to load applications', 'error'))
        .finally(() => (this.loading = false))
    },
    tally (rows) {
      const counts = { '': rows.length, pending: 0, approved: 0, rejected: 0 }
      rows.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1 })
      return counts
    },
    openDetail (item) {
      this.selected = { ...item }
      this.showDetail = true
    },
    openApprove (item) {
      this.selected = { ...item }
      this.showApprove = true
    },
    onApproved () {
      this.showApprove = false
      this.initialize()
      this.notify('Application approved')
    },
    askReject (item) {
      this.confirm = {
        show: true,
        title: 'Reject application',
        message: 'This application will be marked as rejected:',
        itemName: item.company_name,
        loading: false,
        target: item
      }
    },
    async doReject () {
      this.confirm.loading = true
      await apiService.post(`admin/partner-applications/${this.confirm.target.id}/reject`, {})
        .then(() => {
          this.confirm.show = false
          this.initialize()
          this.notify('Application rejected')
        })
        .catch(() => this.notify('Failed to reject application', 'error'))
        .finally(() => (this.confirm.loading = false))
    }
  }
}
</script>

<style scoped>
.pa-filters {
  display: flex;
  gap: 8px;
  padding: 0 4px 12px;
}
.pa-filter {
  font-size: .78rem;
  font-weight: 600;
  color: var(--ec-text-2);
  background: var(--ec-gray-bg);
  border-radius: 6px;
  padding: 5px 12px;
  transition: all .15s ease;
}
.pa-filter--on {
  color: #fff;
  background: #0ea5e9;
}
.pa-filter__count {
  margin-left: 6px;
  opacity: .75;
}
.pa-email {
  font-size: .76rem;
  color: var(--ec-text-2);
  text-decoration: none;
}
</style>
